/** Thrown when a JSON endpoint answers with a non-2xx status. */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Request failed with status ${status}`)
  }
}

/**
 * The current CSRF token. Rails refreshes the XSRF-TOKEN cookie on every response, so it stays
 * valid after the session changes (login), unlike the meta tag from the first page load.
 */
export function csrfToken(): string {
  const cookie = document.cookie.split("; ").find((entry) => entry.startsWith("XSRF-TOKEN="))
  if (cookie) return decodeURIComponent(cookie.slice("XSRF-TOKEN=".length))

  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (!meta) throw new Error("Missing CSRF token")
  return meta
}

type Route = { url: string; method: "get" | "post" | "put" | "patch" | "delete" }

type RequestOptions = {
  body?: Record<string, unknown> | FormData
  signal?: AbortSignal
}

/**
 * Calls a JSON endpoint outside of an Inertia visit, for data that does not belong to the page props.
 * Resolves with the parsed body, or `undefined` for an empty response.
 */
export async function requestJson<T>(route: Route, { body, signal }: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" }
  if (route.method !== "get") headers["X-CSRF-Token"] = csrfToken()
  if (body && !(body instanceof FormData)) headers["Content-Type"] = "application/json"

  const response = await fetch(route.url, {
    method: route.method.toUpperCase(),
    headers,
    credentials: "same-origin",
    body: body instanceof FormData ? body : body && JSON.stringify(body),
    signal,
  })

  const text = await response.text()
  const parsed: unknown = text ? JSON.parse(text) : undefined
  if (!response.ok) throw new HttpError(response.status, parsed)
  return parsed as T
}

/**
 * Downloads the file a GET endpoint sends as an attachment and saves it under the name from
 * its Content-Disposition header. Rejects with an HttpError when the endpoint answers with an
 * error, and with an Error when it sends something other than `contentType`.
 */
export async function downloadFile(route: Route, contentType: string): Promise<void> {
  const response = await fetch(route.url, { headers: { Accept: contentType }, credentials: "same-origin" })

  if (!response.ok) {
    const text = await response.text()
    throw new HttpError(response.status, text ? JSON.parse(text) : undefined)
  }
  if (!response.headers.get("Content-Type")?.startsWith(contentType)) {
    throw new Error(`Expected ${contentType} from ${route.url}`)
  }

  const filename = response.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1]
  if (!filename) throw new Error(`No file name in the response from ${route.url}`)

  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
